import { useEffect, useRef, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PenLine, Eraser, Split, Layers, CheckCircle2 } from "lucide-react";

/**
 * SplitDeliveryDialog — deliver a subset of a parcel's items.
 * Only rendered when parcel.allow_split_delivery AND parcel.items[].
 * Runs a signature pad (mirrors DeliveryReceiptDialog) and posts to /deliver-split.
 */
export default function SplitDeliveryDialog({ open, onOpenChange, parcel, onDelivered }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [signedBy, setSignedBy] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const [chosen, setChosen] = useState({}); // key = type, value = count

  // Compute remaining per type
  const totals = {};
  (parcel?.items || []).forEach((it) => {
    totals[it.type] = (totals[it.type] || 0) + Number(it.count || 0);
  });
  const already = {};
  (parcel?.delivered_items || []).forEach((leg) => {
    (leg.items || []).forEach((it) => {
      already[it.type] = (already[it.type] || 0) + Number(it.count || 0);
    });
  });
  const remaining = Object.fromEntries(
    Object.keys(totals).map((t) => [t, (totals[t] || 0) - (already[t] || 0)])
  );
  const remainingTotal = Object.values(remaining).reduce((n, v) => n + Math.max(0, v), 0);
  const chosenTotal = Object.values(chosen).reduce((n, v) => n + (Number(v) || 0), 0);

  useEffect(() => {
    if (!open) return;
    setSignedBy(parcel?.recipient_name || "");
    setRemarks("");
    setChosen({});
    setTimeout(() => clear(), 50);
    // eslint-disable-next-line
  }, [open, parcel]);

  const point = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = point(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move  = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = point(e); ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.lineTo(p.x, p.y); ctx.stroke(); setHasStroke(true); };
  const end   = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#DDD"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(20, c.height - 20); ctx.lineTo(c.width - 20, c.height - 20); ctx.stroke();
    setHasStroke(false);
  };

  const submit = async () => {
    if (chosenTotal <= 0) return toast.error("Select at least one item to deliver");
    if (!hasStroke) return toast.error("Please capture a signature");
    if (!signedBy) return toast.error("Signed-by name required");
    setBusy(true);
    try {
      const signature_base64 = canvasRef.current.toDataURL("image/png");
      const items = Object.entries(chosen)
        .filter(([, c]) => Number(c) > 0)
        .map(([type, count]) => ({ type, count: Number(count) }));
      const { data } = await api.post(`/parcels/${parcel.id}/deliver-split`, {
        signed_by: signedBy, remarks, signature_base64, items,
      });
      toast.success(data.fully_delivered ? "Fully delivered ✓" : `Split delivered · ${data.delivered_total}/${data.expected_total}`);
      onOpenChange(false);
      onDelivered?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="split-deliver-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Split size={18} className="text-primary" /> Deliver items
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Choose which items you're handing over now. The rest stay open for a later delivery.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border border-border rounded-md p-3 bg-muted/20">
            <div className="flex items-center justify-between mb-2">
              <div className="overline flex items-center gap-1.5"><Layers size={12} className="text-primary" /> Remaining items</div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                Selected <span className="mono text-primary font-bold">{chosenTotal}</span> / {remainingTotal}
              </div>
            </div>
            <div className="space-y-2">
              {Object.keys(totals).map((type) => {
                const rem = remaining[type] || 0;
                if (rem <= 0) return (
                  <div key={type} className="flex items-center gap-2 text-xs opacity-60" data-testid={`split-row-${type}`}>
                    <CheckCircle2 size={14} className="text-emerald-600" />
                    <span className="font-semibold">{type}</span>
                    <span className="mono text-muted-foreground">· all delivered</span>
                  </div>
                );
                const val = chosen[type] ?? 0;
                return (
                  <div key={type} className="flex items-center gap-2" data-testid={`split-row-${type}`}>
                    <span className="text-sm font-semibold flex-1">{type}</span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">remaining {rem}</span>
                    <div className="inline-flex items-center border border-border rounded-md overflow-hidden">
                      <button type="button" onClick={() => setChosen((c) => ({ ...c, [type]: Math.max(0, (c[type] || 0) - 1) }))}
                        data-testid={`split-minus-${type}`}
                        className="w-8 h-8 hover:bg-primary/10 text-lg font-bold">−</button>
                      <Input
                        data-testid={`split-count-${type}`}
                        type="number" min="0" max={rem}
                        value={val}
                        onChange={(e) => {
                          const n = Math.max(0, Math.min(rem, parseInt(e.target.value) || 0));
                          setChosen((c) => ({ ...c, [type]: n }));
                        }}
                        className="w-14 text-center rounded-none border-0 border-x border-border font-mono h-8 p-0"
                      />
                      <button type="button" onClick={() => setChosen((c) => ({ ...c, [type]: Math.min(rem, (c[type] || 0) + 1) }))}
                        data-testid={`split-plus-${type}`}
                        className="w-8 h-8 hover:bg-primary/10 text-lg font-bold">+</button>
                    </div>
                    <button type="button" onClick={() => setChosen((c) => ({ ...c, [type]: rem }))}
                      data-testid={`split-all-${type}`}
                      className="text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline">All</button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="split-signed-by" value={signedBy} onChange={(e) => setSignedBy(e.target.value)} placeholder="Signed by (recipient name)" className="rounded-md" />
            <Input data-testid="split-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)" className="rounded-md" />
          </div>

          <div>
            <div className="overline mb-1 flex items-center justify-between">
              <span className="flex items-center gap-1"><PenLine size={12} /> Signature</span>
              <button type="button" onClick={clear} data-testid="split-clear-sig" className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground hover:text-primary inline-flex items-center gap-1"><Eraser size={11} /> Clear</button>
            </div>
            <canvas
              ref={canvasRef}
              width={720} height={220}
              data-testid="split-sig-canvas"
              className="w-full border border-border rounded-md bg-white touch-none"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-md" data-testid="split-cancel">Cancel</Button>
            <Button type="button" onClick={submit} disabled={busy || chosenTotal <= 0} className="rounded-md bg-primary text-primary-foreground" data-testid="split-submit">
              {busy ? "Saving…" : `Deliver ${chosenTotal} item${chosenTotal === 1 ? "" : "s"} →`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
